import {useEffect, useState} from 'react';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from '@common/utils/hooks/local-storage';

/**
 * Carries a trial code (and the ad it came from) from the /try landing page
 * through signup and back.
 *
 * Kept separate from the global TRACKING_PARAMETERS cookie on purpose: that
 * cookie feeds the Google Ads conversion pipeline and is overwritten on every
 * route change, so the trial never reads or writes it.
 */

const KEY = 'be.trial.pending';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Query-string keys worth keeping for campaign attribution. */
export const TRIAL_TRACKING_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'gad_campaignid',
] as const;

export interface PendingTrial {
  code: string;
  tracking: Record<string, string>;
  savedAt: number;
}

export function savePendingTrial(
  code: string,
  searchParams?: URLSearchParams,
): void {
  const tracking: Record<string, string> = {};
  TRIAL_TRACKING_KEYS.forEach(key => {
    const value = searchParams?.get(key);
    if (value) {
      tracking[key] = value.slice(0, 255);
    }
  });

  // keep attribution captured on an earlier visit when this one carries none
  const previous = getPendingTrial();
  const keepPrevious =
    previous &&
    previous.code === code &&
    Object.keys(tracking).length === 0;

  setInLocalStorage<PendingTrial>(KEY, {
    code,
    tracking: keepPrevious ? previous.tracking : tracking,
    savedAt: Date.now(),
  });
  notifyListeners();
}

export function getPendingTrial(): PendingTrial | null {
  const value = getFromLocalStorage<PendingTrial | null>(KEY, null);
  if (!value || typeof value.code !== 'string' || !value.code) {
    return null;
  }
  if (!value.savedAt || Date.now() - value.savedAt > MAX_AGE_MS) {
    removeFromLocalStorage(KEY);
    return null;
  }
  return {...value, tracking: value.tracking ?? {}};
}

export function clearPendingTrial(): void {
  removeFromLocalStorage(KEY);
  notifyListeners();
}

const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach(listener => listener());
}

/**
 * The remembered trial code, kept current as it is saved or cleared anywhere
 * on the page.
 */
export function usePendingTrial(): PendingTrial | null {
  const [pending, setPending] = useState<PendingTrial | null>(getPendingTrial);

  useEffect(() => {
    const listener = () => setPending(getPendingTrial());
    listeners.add(listener);
    // saved between the first render and this effect
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return pending;
}
